import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiRefresh } from "@mdi/js";
import Stepper from "../components/search/Stepper";
import TypeGrid from "../components/search/TypeGrid";
import TripDetailsForm from "../components/search/TripDetailsForm";
import { useSearch, STEPS } from "../context/SearchContext";
import Header from "../components/Header";
import Footer from "../components/Footer";

/* ---------- Stage wrapper ---------- */
function StageCard({ title, subtitle, children, actions }) {
  return (
    <section className="relative rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 md:p-7 shadow-sm">
      <div
        className="absolute inset-x-0 -top-px h-[3px] rounded-t-2xl"
        style={{ background: "var(--brand-gradient)" }}
      />
      <header className="mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </header>

      <div>{children}</div>

      {actions && (
        <div className="mt-6 flex items-center justify-end">
          <div className="inline-flex w-full flex-wrap items-center justify-end gap-2">{actions}</div>
        </div>
      )}
    </section>
  );
}

export default function Discover() {
  const { types, step: computedStep, clearSelections } = useSearch();

  // Local display step so users can go back without clearing selections
  const [displayStep, setDisplayStep] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const step = useMemo(
    () => Math.min(displayStep, computedStep),
    [displayStep, computedStep]
  );

  const goPrev = () => setDisplayStep((s) => Math.max(0, s - 1));
  const goNext = () => setDisplayStep((s) => Math.min(1, s + 1));

  const canNextFrom0 = types.length > 0;
  const isTripStep = step === 1; // hide hero on trip form step

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      {/* Hero — only for Type step */}
      {!isMobile && !isTripStep && (
        <section
          className="relative border-b border-slate-200"
          style={{
            background:
              `radial-gradient(900px 360px at 85% -30%, var(--accent-500)16, transparent 60%),` +
              `radial-gradient(650px 300px at 10% -20%, var(--blackish)10, transparent 60%),` +
              `linear-gradient(180deg, white, var(--fog))`,
          }}
        >
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Reserve your <span className="text-accent-500">perfect</span> cars
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Start by selecting the vehicle type you need, then complete trip
              details for smart matching, clear pricing, and easy checkout.
            </p>
            <div
              className="mt-6 h-[3px] w-40 rounded-full"
              style={{ background: "var(--brand-gradient)" }}
            />
          </div>
        </section>
      )}

      <main className="bg-white">
        {isMobile ? (
          <div className="mx-auto max-w-3xl px-4 py-5 pb-28">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="mobile-step0"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <section>
                    <header className="mb-3">
                      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
                        Pick vehicle type
                      </h1>
                      <p className="mt-2 text-sm text-slate-600">
                        Select one or more vehicle types to continue.
                      </p>
                    </header>

                    <TypeGrid />
                  </section>

                  <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pt-3 backdrop-blur">
                    <div className="mx-auto flex max-w-3xl items-center gap-2">
                      <button
                        type="button"
                        onClick={goPrev}
                        disabled
                        className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-400"
                        aria-disabled="true"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={clearSelections}
                        className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700"
                        title="Clear vehicle type selection"
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        disabled={!canNextFrom0}
                        className={`flex-1 rounded-xl px-4 py-3 text-sm font-semibold text-white ${
                          canNextFrom0
                            ? "bg-black"
                            : "bg-slate-400 cursor-not-allowed"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="mobile-step1"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <section>
                    <header className="mb-3">
                      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
                        Trip details
                      </h1>
                    </header>

                    <TripDetailsForm
                      mobileMode
                      mobileFooterActions={
                        <>
                          <button
                            type="button"
                            onClick={goPrev}
                            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-300 text-slate-700"
                            aria-label="Back"
                          >
                            <Icon path={mdiChevronLeft} size={0.9} />
                          </button>
                          <button
                            type="button"
                            onClick={clearSelections}
                            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-300 text-slate-700"
                            title="Clear vehicle type selection"
                            aria-label="Reset"
                          >
                            <Icon path={mdiRefresh} size={0.85} />
                          </button>
                        </>
                      }
                    />
                  </section>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            {/* Stepper */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <Stepper
                steps={STEPS}
                current={step}
                onStepChange={(idx) => {
                  if (idx < step) setDisplayStep(idx); // allow jumping back only
                }}
              />
            </div>

            {/* Stage */}
            <div className="mt-8 relative">
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div
                    key="step0"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <StageCard
                      title="Pick vehicle type"
                      subtitle="Select one or more vehicle types to continue."
                      actions={
                        <>
                          <span className="mr-0 hidden md:inline text-sm text-slate-600 lg:mr-4">
                            {types.length
                              ? `${types.length} selected`
                              : "No type selected"}
                          </span>

                          <button
                            type="button"
                            onClick={goPrev}
                            disabled
                            className="cursor-not-allowed rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-400"
                            aria-disabled="true"
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            onClick={clearSelections}
                            className="rounded-xl border border-transparent px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                            title="Clear vehicle type selection"
                          >
                            Reset
                          </button>

                          <button
                            type="button"
                            onClick={goNext}
                            disabled={!canNextFrom0}
                            className={`rounded-xl px-5 py-2 text-sm font-semibold text-white ${
                              canNextFrom0
                                ? "bg-black"
                                : "cursor-not-allowed bg-slate-400"
                            }`}
                          >
                            Next
                          </button>
                        </>
                      }
                    >
                      <TypeGrid />
                    </StageCard>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <StageCard
                      title="Trip details"
                      subtitle="Add pickup/drop, stops, dates & times, and a search radius."
                      actions={
                        <>
                          <button
                            type="button"
                            onClick={goPrev}
                            className="rounded-xl border border-slate-300 px-4 py-2 text-sm"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={clearSelections}
                            className="rounded-xl border border-transparent px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                            title="Clear vehicle type selection"
                          >
                            Reset
                          </button>
                        </>
                      }
                    >
                      <TripDetailsForm />
                    </StageCard>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </main>

      {!isMobile && <Footer />}
    </div>
  );
}
